import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { InjectableSuperclass } from '@sama/ng-core';
import { firstValueFrom } from 'rxjs';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../../team-apis/core/shell-connector';
import { CuboidService } from '../../render/mesh/cuboid/cuboid.service';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { getShapeForEventTracking } from '../misc-utils';
import { interpolateLocation } from '../point-cloud-interpolation-helper';
import { CuboidRescaleConfirmationModalComponent } from './cuboid-rescale-confirmation-modal/cuboid-rescale-confirmation-modal.component';
import {
  CuboidRescaleModalComponent,
  CuboidRescaleModalConfig,
  CuboidRescaleResponse,
} from './cuboid-rescale-modal/cuboid-rescale-modal.component';

export type RescaleMethod = 'rescale_all_frames' | 'rescale_current_frame';

@Injectable()
export class CuboidRescaleService extends InjectableSuperclass {
  constructor(
    private matDialog: MatDialog,
    @Inject(shellConnectorToken)
    private shellConnector: ShellConnector,
    private store: PointCloudContentStore,
    private cuboidService: CuboidService,
  ) {
    super();
  }

  async rescaleInAllFramesWithConfirmation(cuboidIndex: number) {
    const modalRef = this.matDialog.open(
      CuboidRescaleConfirmationModalComponent,
    );

    const shouldRescale = await firstValueFrom(modalRef.afterClosed());
    if (!shouldRescale) {
      return false;
    }
    this.rescaleInAllFrames(cuboidIndex);
    return this.trackRescale(cuboidIndex, 'side-panel', 'rescale_all_frames');
  }

  async rescaleWithModal(cuboidIndex: number) {
    const cuboid = this.cuboidService.getHelper(cuboidIndex).box;
    const modalRef = this.matDialog.open<
      CuboidRescaleModalComponent,
      CuboidRescaleModalConfig,
      CuboidRescaleResponse
    >(CuboidRescaleModalComponent, {
      minWidth: '450px',
      data: {
        width: cuboid.scale.x,
        length: cuboid.scale.y,
        height: cuboid.scale.z,
      },
    });

    const response = await firstValueFrom(modalRef.afterClosed());
    if (!response) {
      return false;
    }

    this.store('rescaleAllFrames').set(
      response.rescaleMethod === 'rescale_all_frames',
    );
    cuboid.scale.fromArray(response.dimensions);
    this.store.updateFocusedLocationFromCuboidMesh(cuboid);
    return this.trackRescale(cuboidIndex, 'modal', response.rescaleMethod);
  }

  rescaleInAllFrames(cuboidIndex: number) {
    const currentFrame = this.store.state().render.frameNumber;
    const shapeStore = this.store('render')('cuboids')(cuboidIndex);
    const keyLocations = shapeStore.state().key_locations;

    const currentScale = interpolateLocation(keyLocations, currentFrame).scale;

    this.store.batch(() => {
      for (let i = keyLocations.length; --i >= 0; ) {
        shapeStore('key_locations')(i)('scale').set(currentScale);
      }
    });
  }

  private trackRescale(
    cuboidIndex: number,
    source: string,
    method: RescaleMethod,
  ) {
    const shapeStore = this.store('render')('cuboids')(cuboidIndex);
    this.shellConnector.pushAndTrack({
      id: 'edit_shape_points',
      bucket: 'adjustment',
      method,
      source,
      shapes: [getShapeForEventTracking(shapeStore.state())],
    });
    return true;
  }
}
